import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supervisorAPI, BACKEND_URL } from '../../utils/api';
import { getGPSLocation } from '../../utils/camera';
import { toast } from 'sonner';
import { 
  MapPin, Camera, CheckCircle2, XCircle, 
  Clock, Users, Wrench, Sparkles, FileText, ArrowLeft, UserPlus, X, UserX, Navigation, Edit2, Plus, RefreshCw, ClipboardList
} from 'lucide-react';

// Helper function to compress image before upload
const compressImage = async (dataUrl, maxWidth = 1200, quality = 0.7) => {
  console.log('[CompressImage] Starting compression, dataUrl type:', typeof dataUrl, 'length:', dataUrl?.length);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      console.log('[CompressImage] Image loaded:', img.width, 'x', img.height);
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Resize if larger than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to JPEG with quality
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      console.log('[CompressImage] Done, output length:', compressedDataUrl.length);
      resolve(compressedDataUrl);
    };
    img.onerror = (err) => {
      console.error('[CompressImage] Image load error:', err);
      reject(new Error('Failed to load image for compression'));
    };
    img.src = dataUrl;
  });
};

// Helper function to add date/time stamp to photo
const addTimestampToPhoto = async (dataUrl, isSelfie = false) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // Mirror for selfie
      if (isSelfie) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(img, 0, 0);
      if (isSelfie) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
      
      // Get date/time in IST
      const now = new Date();
      const dateStr = now.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
      
      // Draw stamp
      const fontSize = Math.max(16, Math.floor(canvas.width / 20));
      ctx.font = `bold ${fontSize}px Arial`;
      const textW = ctx.measureText(dateStr).width;
      const pad = 8;
      const boxX = 10, boxY = canvas.height - fontSize - pad * 2 - 10;
      
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(boxX, boxY, textW + pad * 2, fontSize + pad * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.textBaseline = 'top';
      ctx.fillText(dateStr, boxX + pad, boxY + pad);
      
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};


const Inspection = () => {
  const { parkId, inspectionId: editInspectionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Edit mode detection
  const isEditMode = !!editInspectionId;
  
  // Refs for file inputs
  const selfiePhotoRef = useRef(null);
  const workersPhotoRef = useRef(null);
  const cleanlinessPhotoRef = useRef(null);
  const closedPhotoRef = useRef(null);
  const additionalPhotoRef = useRef(null);

  // Steps: 'selfie' -> 'status' -> 'open-form' or 'closed-form' -> 'questionnaires' -> 'submit'
  const [step, setStep] = useState(isEditMode ? 'loading' : 'selfie');
  const [loading, setLoading] = useState(false);
  const [inspectionId, setInspectionId] = useState(editInspectionId || null);
  const [parkData, setParkData] = useState(null);
  const [editMinutesRemaining, setEditMinutesRemaining] = useState(0);
  
  // Selfie photo (required before starting)
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  
  const [parkStatus, setParkStatus] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [nobodyPresent, setNobodyPresent] = useState(false);
  const [temporaryWorkers, setTemporaryWorkers] = useState([]);
  const [showAddTempWorker, setShowAddTempWorker] = useState(false);
  const [newTempWorker, setNewTempWorker] = useState({ name: '', reason: '', photo: null });
  const [tempWorkerPhotos, setTempWorkerPhotos] = useState([]);  // Photos for temp workers when >2
  const [toolsAvailable, setToolsAvailable] = useState(null);
  const [cleanliness, setCleanliness] = useState(null);
  const [remarks, setRemarks] = useState('');
  
  const [workersPhoto, setWorkersPhoto] = useState(null);
  const [cleanlinessPhotos, setCleanlinessPhotos] = useState([]);  // Up to 3 cleanliness photos
  const [closedPhoto, setClosedPhoto] = useState(null);
  const [additionalPhotos, setAdditionalPhotos] = useState([]);  // For parks with >5 workers
  
  const [gpsStatus, setGpsStatus] = useState('acquiring');
  
  // Dynamic Questionnaires
  const [activeQuestionnaires, setActiveQuestionnaires] = useState([]);
  const [questionnaireResponses, setQuestionnaireResponses] = useState({});
  
  // Check if park needs additional photos (>=5 workers or >2 temp workers)
  const needsAdditionalPhotos = (parkData?.manpower_list?.length || 0) >= 5 || temporaryWorkers.length > 2;

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/supervisor/login');
      return;
    }
    
    // Fetch active questionnaires on mount
    fetchActiveQuestionnaires();
    
    // If edit mode, load existing inspection data
    if (isEditMode && editInspectionId) {
      loadExistingInspection();
    } else {
      // Get GPS location on mount for new inspection
      acquireGPS();
    }
  }, [user, navigate, isEditMode, editInspectionId, authLoading]);

  const loadExistingInspection = async () => {
    setLoading(true);
    try {
      const response = await supervisorAPI.getInspection(editInspectionId);
      const data = response.data;
      
      // Only allow editing completed inspections within 10-minute window
      if (!data.can_edit) {
        toast.error('Edit window has expired');
        navigate('/supervisor/dashboard');
        return;
      }
      
      // If this is an incomplete inspection (no end_time), redirect to fresh start
      if (data.edit_minutes_remaining === -1) {
        toast.info('Starting fresh inspection...');
        navigate(`/supervisor/inspection/${data.park_id}`);
        return;
      }
      
      // Populate form with existing data for editing submitted inspection
      setInspectionId(data.inspection_id);
      setParkData({
        park_id: data.park_id,
        park_name: data.park_name,
        zone: data.zone,
        division: data.division,
        manpower_list: data.manpower_list || [],
        google_maps_link: data.google_maps_link
      });
      setSelfiePhoto(data.selfie_photo_url);
      setSelectedEmployees(data.selected_employees || []);
      setTemporaryWorkers(data.temporary_workers || []);
      setToolsAvailable(data.tools_available);
      setCleanliness(data.cleanliness);
      setRemarks(data.remarks || '');
      setWorkersPhoto(data.workers_photo_url);
      // Load cleanliness photos (new array format or legacy single photo)
      if (data.cleanliness_photos && data.cleanliness_photos.length > 0) {
        setCleanlinessPhotos(data.cleanliness_photos);
      } else if (data.cleanliness_photo_url) {
        setCleanlinessPhotos([data.cleanliness_photo_url]);
      }
      setClosedPhoto(data.closed_photo_url);
      setEditMinutesRemaining(data.edit_minutes_remaining);
      setNobodyPresent(data.nobody_present || false);
      
      // Set park status and navigate to correct form
      const savedParkStatus = data.park_status;
      if (savedParkStatus) {
        setParkStatus(savedParkStatus);
        if (savedParkStatus === 'Open') {
          setStep('open-form');
        } else if (savedParkStatus === 'Closed') {
          setStep('closed-form');
        }
      } else {
        setStep('status');
      }
      
      // Acquire GPS for submit
      acquireGPS();
      
      toast.info(`Editing inspection - ${data.edit_minutes_remaining} minutes remaining`, { id: 'edit-toast' });
    } catch (error) {
      toast.error('Failed to load inspection for editing');
      navigate('/supervisor/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Fetch active questionnaires
  const fetchActiveQuestionnaires = async () => {
    try {
      const res = await supervisorAPI.getActiveQuestionnaires();
      const questionnaires = res.data.questionnaires || [];
      setActiveQuestionnaires(questionnaires);
      // Initialize responses object
      const initResponses = {};
      questionnaires.forEach(q => {
        initResponses[q.questionnaire_id] = {};
        q.questions.forEach(question => {
          initResponses[q.questionnaire_id][question.question_id] = '';
        });
      });
      setQuestionnaireResponses(initResponses);
      return questionnaires;
    } catch (error) {
      console.error('Failed to fetch questionnaires:', error);
      return [];
    }
  };

  const acquireGPS = async () => {
    setGpsStatus('acquiring');
    try {
      const location = await getGPSLocation();
      if (location && location.lat && location.lng) {
        setGpsLocation(location);
        setGpsStatus('acquired');
        return location;
      } else {
        throw new Error('Invalid GPS data');
      }
    } catch (error) {
      console.error('GPS Error:', error);
      setGpsStatus('error');
      toast.error('Unable to get GPS location. Please enable location services and try again.');
      return null;
    }
  };

  // Retry GPS acquisition
  const retryGPS = async () => {
    toast.info('Retrying GPS...');
    setGpsStatus('acquiring');
    
    // Small delay before retrying
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const location = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy
            });
          },
          (error) => {
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          }
        );
      });
      
      setGpsLocation(location);
      setGpsStatus('acquired');
      toast.success('GPS location acquired!');
      return location;
    } catch (error) {
      console.error('GPS Retry Error:', error);
      setGpsStatus('error');
      
      let errorMsg = 'Unable to get GPS location.';
      if (error.code === 1) {
        errorMsg = 'Location permission denied. Please enable location access in your browser settings.';
      } else if (error.code === 2) {
        errorMsg = 'Location unavailable. Please check your GPS/network settings.';
      } else if (error.code === 3) {
        errorMsg = 'Location request timed out. Please try again.';
      }
      
      toast.error(errorMsg);
      return null;
    }
  };

  // Handle selfie photo capture (new or update)
  const handleSelfiePhoto = async (event, isUpdate = false) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setLoading(true);
    const loadingToast = toast.loading('Uploading selfie...');
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          let imageData = e.target.result;
          console.log('[handleSelfiePhoto] Processing selfie');
          
          // FIRST: Add timestamp and mirror for selfie BEFORE compression
          try {
            console.log('[handleSelfiePhoto] Adding timestamp...');
            imageData = await addTimestampToPhoto(imageData, true); // true = mirror selfie
            console.log('[handleSelfiePhoto] Timestamp added!');
          } catch (stampErr) {
            console.error('[handleSelfiePhoto] Timestamp failed:', stampErr);
          }
          
          // THEN: Compress the stamped image
          try {
            imageData = await compressImage(imageData, 800, 0.7);
            console.log('[handleSelfiePhoto] Compressed');
          } catch (compressErr) {
            console.warn('[handleSelfiePhoto] Compression failed:', compressErr);
          }
          
          // Upload selfie photo with retry
          let response;
          let retries = 2;
          while (retries >= 0) {
            try {
              response = await supervisorAPI.uploadPhoto({
                image_data: imageData,
                filename: 'selfie.jpg',
                inspection_id: `selfie_${parkId}_${Date.now()}`,
                photo_type: 'selfie'
              });
              break; // Success
            } catch (uploadErr) {
              if (retries === 0) throw uploadErr;
              console.log(`Upload failed, retrying... (${retries} left)`);
              retries--;
              await new Promise(r => setTimeout(r, 1000)); // Wait 1s before retry
            }
          }
          
          const photoUrl = response.data.photo_url;
          setSelfiePhoto(`${photoUrl}${photoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
          
          if (isUpdate) {
            toast.success('Selfie updated!', { id: loadingToast });
          } else {
            // New inspection - start the inspection after selfie
            toast.success('Selfie uploaded!', { id: loadingToast });
            await startInspection(response.data.photo_url);
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          const errorMsg = uploadError.response?.data?.detail || 'Failed to upload selfie. Please check your network and try again.';
          toast.error(errorMsg, { id: loadingToast });
        } finally {
          setLoading(false);
          toast.dismiss(loadingToast);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read photo file', { id: loadingToast });
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Photo error:', error);
      toast.error('Failed to process photo');
      setLoading(false);
    }
    event.target.value = '';
  };

  const startInspection = async (selfieUrl) => {
    setLoading(true);
    try {
      let location = gpsLocation;
      if (!location) {
        location = await getGPSLocation();
        setGpsLocation(location);
      }
      
      const response = await supervisorAPI.startInspection({
        supervisor_id: user.supervisor_id,
        park_id: parkId,
        gps_location: location,
        selfie_photo_url: selfieUrl
      });
      
      setInspectionId(response.data.inspection_id);
      setParkData({
        park_id: parkId,
        park_name: response.data.park_name,
        zone: response.data.zone,
        division: response.data.division,
        manpower_list: response.data.manpower_list || [],
        google_maps_link: response.data.google_maps_link
      });
      
      // Always start fresh - go to status selection
      setStep('status');
      toast.success('Inspection started!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start inspection');
    } finally {
      setLoading(false);
    }
  };

  // Handle photo from file input
  const handlePhotoFile = async (event, photoType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!inspectionId && photoType !== 'selfie') {
      console.error('[handlePhotoFile] Missing inspectionId for', photoType);
      toast.error('Session error: Inspection ID missing. Please refresh.');
      return;
    }

    setLoading(true);
    const loadingToast = toast.loading(`Uploading ${photoType} photo...`);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          let imageData = e.target.result;
          console.log('[handlePhotoFile] Processing', photoType, 'photo, file size:', file.size);
          
          // FIRST: Add timestamp BEFORE compression
          try {
            imageData = await addTimestampToPhoto(imageData, false);
          } catch (stampErr) {
            console.error('[handlePhotoFile] Timestamp failed:', stampErr);
          }
          
          // THEN: Compress
          try {
            imageData = await compressImage(imageData, 1200, 0.7);
          } catch (compressErr) {
            console.warn('[handlePhotoFile] Compression failed:', compressErr);
          }
          
          // Use unique photo type to prevent caching issues
          const uniquePhotoType = `${photoType}_${Date.now()}`;
          
          const response = await supervisorAPI.uploadPhoto({
            image_data: imageData,
            filename: `${uniquePhotoType}.jpg`,
            inspection_id: inspectionId,
            photo_type: uniquePhotoType
          });
          
          const photoUrl = response.data.photo_url;
          if (photoType === 'workers') {
            setWorkersPhoto(photoUrl);
            toast.success('Workers photo updated!', { id: loadingToast });
          } else if (photoType === 'closed') {
            setClosedPhoto(photoUrl);
            toast.success('Closed park photo updated!', { id: loadingToast });
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error('Failed to upload photo. Please try again.', { id: loadingToast });
        } finally {
          setLoading(false);
          toast.dismiss(loadingToast);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read photo file', { id: loadingToast });
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Photo error:', error);
      toast.error('Failed to process photo', { id: loadingToast });
      setLoading(false);
    }
    event.target.value = '';
  };

  // Handle cleanliness photo capture (up to 3 photos)
  const handleCleanlinessPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (cleanlinessPhotos.length >= 3) {
      toast.error('Maximum 3 cleanliness photos allowed');
      e.target.value = '';
      return;
    }
    
    if (!inspectionId) {
      toast.error('Please start the inspection first');
      e.target.value = '';
      return;
    }
    
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          let imageData = event.target.result;
          console.log('[handleCleanlinessPhoto] Processing photo');
          
          // FIRST: Add timestamp BEFORE compression
          try {
            console.log('[handleCleanlinessPhoto] Adding timestamp...');
            imageData = await addTimestampToPhoto(imageData, false);
            console.log('[handleCleanlinessPhoto] Timestamp added!');
          } catch (stampErr) {
            console.error('[handleCleanlinessPhoto] Timestamp failed:', stampErr);
          }
          
          // THEN: Compress the stamped image
          try {
            imageData = await compressImage(imageData, 1000, 0.7);
            console.log('[handleCleanlinessPhoto] Compressed');
          } catch (compressErr) {
            console.warn('[handleCleanlinessPhoto] Compression failed:', compressErr);
          }
          
          const photoNum = cleanlinessPhotos.length + 1;
          // Use unique photo type to prevent caching issues
          const uniquePhotoType = `cleanliness_${photoNum}_${Date.now()}`;
          
          // Upload with retry
          let response;
          let retries = 2;
          while (retries >= 0) {
            try {
              response = await supervisorAPI.uploadPhoto({
                inspection_id: inspectionId,
                photo_type: uniquePhotoType,
                filename: `${uniquePhotoType}.jpg`,
                image_data: imageData
              });
              break;
            } catch (uploadErr) {
              if (retries === 0) throw uploadErr;
              retries--;
              await new Promise(r => setTimeout(r, 1000));
            }
          }
          
          console.log('Upload response:', response.data);
          const photoUrl = response.data.photo_url;
          setCleanlinessPhotos(prev => [...prev, photoUrl]);
          toast.success(`Cleanliness photo ${photoNum} captured`, { id: loadingToast });
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload cleanliness photo.', { id: loadingToast });
        } finally {
          setLoading(false);
          toast.dismiss(loadingToast);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read photo file', { id: loadingToast });
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File read error:', error);
      setLoading(false);
      toast.error('Failed to process photo', { id: loadingToast });
    }
    e.target.value = '';
  };

  const removeCleanlinessPhoto = (index) => {
    setCleanlinessPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const triggerPhotoCapture = (photoType) => {
    if (loading) return;
    
    if (photoType === 'selfie' && selfiePhotoRef.current) {
      selfiePhotoRef.current.click();
    } else if (photoType === 'workers' && workersPhotoRef.current) {
      workersPhotoRef.current.click();
    } else if (photoType === 'cleanliness' && cleanlinessPhotoRef.current) {
      cleanlinessPhotoRef.current.click();
    } else if (photoType === 'closed' && closedPhotoRef.current) {
      closedPhotoRef.current.click();
    }
  };

  const handleStatusSelect = async (status) => {
    setParkStatus(status);
    
    if (status === 'Open') {
      setStep('open-form');
    } else {
      setStep('closed-form');
    }
  };

  const handleNobodyPresentToggle = () => {
    if (!nobodyPresent) {
      // When nobody present is selected, clear selected employees
      setSelectedEmployees([]);
      setNobodyPresent(true);
    } else {
      setNobodyPresent(false);
    }
  };

  // Check for questionnaires before final submit
  const proceedToQuestionnairesOrSubmit = async () => {
    // Validation for regular inspection
    if (parkStatus === 'Open') {
      if (!nobodyPresent && selectedEmployees.length === 0 && temporaryWorkers.length === 0) {
        toast.error('Please select present employees or mark "Nobody Present"');
        return;
      }
      if (!nobodyPresent && !workersPhoto) {
        toast.error('Please capture workers photo');
        return;
      }
      if (cleanlinessPhotos.length === 0) {
        toast.error('Please capture at least one cleanliness photo');
        return;
      }
      if (!cleanliness) {
        toast.error('Please select cleanliness level');
        return;
      }
      
      // Validate questionnaire responses if any active questionnaires
      for (const q of activeQuestionnaires) {
        for (const question of q.questions) {
          if (question.is_required) {
            const answer = questionnaireResponses[q.questionnaire_id]?.[question.question_id];
            if (!answer || answer.trim() === '') {
              toast.error(`Please answer: "${question.question_text}"`);
              return;
            }
          }
        }
      }
    } else if (parkStatus === 'Closed') {
      if (!closedPhoto) {
        toast.error('Please capture closed park photo');
        return;
      }
    }

    // Proceed directly to submit
    handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endLocation = await getGPSLocation();
      
      const submissionData = {
        inspection_id: inspectionId,
        park_status: parkStatus,
        selected_employees: parkStatus === 'Open' && !nobodyPresent ? selectedEmployees : [],
        nobody_present: nobodyPresent,
        temporary_workers: parkStatus === 'Open' ? temporaryWorkers : [],
        tools_available: parkStatus === 'Open' ? toolsAvailable : null,
        cleanliness: parkStatus === 'Open' ? cleanliness : null,
        remarks,
        selfie_photo_url: selfiePhoto,
        workers_photo_url: workersPhoto,
        cleanliness_photo_url: cleanlinessPhotos[0] || null,
        cleanliness_photos: cleanlinessPhotos,
        closed_photo_url: closedPhoto,
        end_gps_location: endLocation,
        additional_photos: additionalPhotos,
        temp_worker_photos: tempWorkerPhotos
      };
      
      if (isEditMode) {
        await supervisorAPI.editInspection(inspectionId, submissionData);
        toast.success('Inspection updated successfully!');
      } else {
        await supervisorAPI.submitInspection(submissionData);
        
        // Submit questionnaire responses if any
        if (activeQuestionnaires.length > 0) {
          const responses = activeQuestionnaires.map(q => ({
            questionnaire_id: q.questionnaire_id,
            questionnaire_title: q.title,
            answers: q.questions.map(question => ({
              question_id: question.question_id,
              question_text: question.question_text,
              question_type: question.question_type,
              answer: questionnaireResponses[q.questionnaire_id]?.[question.question_id] || ''
            }))
          }));
          
          await supervisorAPI.submitQuestionnaireResponses({
            inspection_id: inspectionId,
            park_id: parkId,
            park_name: parkData?.park_name || '',
            supervisor_id: user.supervisor_id,
            supervisor_name: user.supervisor_name || user.supervisor_id,
            responses
          });
        }
        
        toast.success('Inspection submitted successfully!');
      }
      
      navigate('/supervisor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'submit'} inspection`);
    } finally {
      setLoading(false);
    }
  };

  // Handler for additional photos
  const handleAdditionalPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (additionalPhotos.length >= 2) {
      toast.error('Maximum 2 additional photos allowed');
      e.target.value = '';
      return;
    }
    
    if (!inspectionId) {
      toast.error('Please start the inspection first');
      e.target.value = '';
      return;
    }
    
    setLoading(true);
    const loadingToast = toast.loading('Uploading additional photo...');
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          let imageData = event.target.result;
          
          // Compress image before upload
          try {
            imageData = await compressImage(imageData, 1000, 0.7);
          } catch (compressErr) {
            console.warn('Frontend compression failed:', compressErr);
          }
          
          // Add timestamp to photo (with GPS)
          try {
            imageData = await addTimestampToPhoto(imageData, false);
            console.log('Timestamp added to additional photo with GPS');
          } catch (stampErr) {
            console.warn('Failed to add timestamp:', stampErr);
          }
          
          const photoNum = additionalPhotos.length + 1;
          const uniquePhotoType = `additional_${photoNum}_${Date.now()}`;
          console.log('Uploading additional photo, inspectionId:', inspectionId);
          
          // Upload with retry
          let response;
          let retries = 2;
          while (retries >= 0) {
            try {
              response = await supervisorAPI.uploadPhoto({
                inspection_id: inspectionId,
                photo_type: uniquePhotoType,
                filename: `${uniquePhotoType}.jpg`,
                image_data: imageData
              });
              break;
            } catch (uploadErr) {
              if (retries === 0) throw uploadErr;
              retries--;
              await new Promise(r => setTimeout(r, 1000));
            }
          }
          
          console.log('Upload response:', response.data);
          const photoUrl = response.data.photo_url;
          setAdditionalPhotos(prev => [...prev, photoUrl]);
          toast.success('Additional photo captured', { id: loadingToast });
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload additional photo', { id: loadingToast });
        } finally {
          setLoading(false);
          toast.dismiss(loadingToast);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read photo file', { id: loadingToast });
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File read error:', error);
      setLoading(false);
      toast.error('Failed to process photo');
    }
    e.target.value = '';
  };

  const removeAdditionalPhoto = (index) => {
    setAdditionalPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const toggleEmployee = (employeeId) => {
    if (nobodyPresent) return; // Can't select if nobody present is checked
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const addTemporaryWorker = () => {
    if (!newTempWorker.name.trim()) {
      toast.error('Please enter worker name');
      return;
    }
    setTemporaryWorkers(prev => [...prev, { ...newTempWorker }]);
    setNewTempWorker({ name: '', reason: '' });
    setShowAddTempWorker(false);
    toast.success('Temporary worker added');
  };

  const removeTemporaryWorker = (index) => {
    setTemporaryWorkers(prev => prev.filter((_, i) => i !== index));
  };

  const openGoogleMaps = () => {
    if (parkData?.google_maps_link) {
      window.open(parkData.google_maps_link, '_blank');
    }
  };

  // Loading state for edit mode
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading inspection for editing...</p>
        </div>
      </div>
    );
  }

  // STEP 1: Selfie capture before starting
  if (step === 'selfie') {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-blue-600 text-white p-4">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button onClick={() => navigate('/supervisor/dashboard')} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">START INSPECTION</h1>
              <p className="text-sm opacity-90">Park ID: {parkId}</p>
            </div>
          </div>
        </div>

        <div className="max-w-md mx-auto p-4">
          <div className="bg-white rounded-xl shadow-sm p-6 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-10 h-10 text-blue-600" />
            </div>
            
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Take Your Selfie
            </h2>
            <p className="text-slate-600 mb-6">
              Please take a selfie photo in front of the park to start the inspection.
            </p>

            {/* GPS Status */}
            <div className={`mb-6 p-4 rounded-lg ${
              gpsStatus === 'acquired' ? 'bg-green-50 text-green-700' :
              gpsStatus === 'error' ? 'bg-yellow-50 text-yellow-700' :
              'bg-yellow-50 text-yellow-700'
            }`}>
              <div className="flex items-center justify-center gap-2">
                <MapPin className="w-5 h-5" />
                {gpsStatus === 'acquiring' && 'Acquiring GPS location...'}
                {gpsStatus === 'acquired' && 'GPS location acquired'}
                {gpsStatus === 'error' && 'GPS location unavailable - continuing without GPS'}
              </div>
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              capture="user"
              ref={selfiePhotoRef}
              onChange={(e) => handleSelfiePhoto(e, false)}
              className="hidden"
            />

            {selfiePhoto ? (
              <div className="mb-4">
                <img src={selfiePhoto} alt="Selfie" className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-green-500" />
                <p className="text-green-600 mt-2 font-semibold">Selfie captured!</p>
              </div>
            ) : (
              <button
                onClick={() => triggerPhotoCapture('selfie')}
                disabled={loading || gpsStatus === 'acquiring'}
                className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Camera className="w-6 h-6" />
                    TAP TO TAKE SELFIE
                  </>
                )}
              </button>
            )}

            <p className="text-xs text-slate-500 mt-4">
              This selfie is required to verify your presence at the park
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: Select park status (Open/Closed)
  if (step === 'status') {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-blue-600 text-white p-4">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button onClick={() => setStep('selfie')} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{parkData?.park_name}</h1>
              <p className="text-sm opacity-90">{parkData?.zone}</p>
            </div>
            {parkData?.google_maps_link && (
              <button onClick={openGoogleMaps} className="p-2 bg-white/20 rounded-lg" title="Navigate">
                <Navigation className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="max-w-md mx-auto p-4 space-y-6">
          {/* Selfie confirmation */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-4">
            <img src={selfiePhoto} alt="Your selfie" className="w-16 h-16 rounded-full object-cover border-2 border-green-500" />
            <div>
              <p className="font-bold text-green-800">Selfie Verified</p>
              <p className="text-sm text-green-600">Inspection started</p>
            </div>
          </div>

          <h2 className="text-lg font-bold text-center text-slate-700">
            What is the park status?
          </h2>

          <button
            onClick={() => handleStatusSelect('Open')}
            className="w-full h-24 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold text-xl flex items-center justify-center gap-3"
          >
            <CheckCircle2 className="w-8 h-8" />
            PARK IS OPEN
          </button>

          <button
            onClick={() => handleStatusSelect('Closed')}
            className="w-full h-24 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold text-xl flex items-center justify-center gap-3"
          >
            <XCircle className="w-8 h-8" />
            PARK IS CLOSED
          </button>
        </div>
      </div>
    );
  }

  // STEP 3A: Closed park form
  if (step === 'closed-form') {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <div className="bg-red-600 text-white p-4">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button onClick={() => setStep('status')} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">PARK CLOSED</h1>
              <p className="text-sm opacity-90">{parkData?.park_name}</p>
            </div>
            {/* Selfie Preview with Edit */}
            {selfiePhoto && (
              <div className="relative">
                <img src={selfiePhoto} alt="Selfie" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                <button
                  onClick={() => document.getElementById('selfie-edit-closed').click()}
                  className="absolute -bottom-1 -right-1 bg-white text-red-600 rounded-full p-0.5"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  id="selfie-edit-closed"
                  onChange={(e) => handleSelfiePhoto(e, true)}
                  className="hidden"
                />
              </div>
            )}
          </div>
        </div>

        <div className="max-w-md mx-auto p-4 space-y-6">
          {/* GPS Status in form */}
          {gpsStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-red-700 text-sm">GPS unavailable</span>
                <button 
                  onClick={retryGPS}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm font-semibold flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry
                </button>
              </div>
            </div>
          )}

          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <p className="text-red-800 font-medium">
              All workers will be marked as <strong>ABSENT</strong> for this park today.
            </p>
          </div>

          {/* Hidden file input for closed photo */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={closedPhotoRef}
            onChange={(e) => handlePhotoFile(e, 'closed')}
            className="hidden"
          />

          <div>
            <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
              Closed Park Photo *
            </label>
            {closedPhoto ? (
              <div className="relative group">
                <img src={closedPhoto} alt="Closed park" className="w-full h-48 object-cover rounded-lg" />
                {loading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <button
                  onClick={() => {
                    setClosedPhoto(null);
                    setTimeout(() => triggerPhotoCapture('closed'), 100);
                  }}
                  disabled={loading}
                  className="absolute top-2 right-2 bg-white rounded-lg px-3 py-2 shadow-md text-sm font-bold disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Retake'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => triggerPhotoCapture('closed')}
                disabled={loading}
                className="h-48 w-full rounded-lg border-2 border-dashed border-slate-300 bg-white hover:bg-slate-50 flex flex-col items-center justify-center gap-2"
              >
                <Camera className="w-12 h-12 text-slate-400" />
                <span className="font-bold text-slate-600">{loading ? 'UPLOADING...' : 'TAP TO CAPTURE'}</span>
              </button>
            )}
          </div>

          <div>
            <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
              Remarks
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full p-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
            />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={proceedToQuestionnairesOrSubmit}
              disabled={loading || !closedPhoto}
              className="w-full h-14 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg disabled:opacity-50"
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT - PARK CLOSED'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 3B: Open park form
  const manpowerList = parkData?.manpower_list || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-green-600 text-white p-4">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button onClick={() => setStep('status')} className="p-2 hover:bg-white/20 rounded-lg">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">PARK OPEN</h1>
            <p className="text-sm opacity-90">{parkData?.park_name}</p>
          </div>
          {parkData?.google_maps_link && (
            <button onClick={openGoogleMaps} className="p-2 bg-white/20 rounded-lg" title="Navigate">
              <Navigation className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-6">
        {/* Employee Attendance */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 block flex items-center gap-2">
            <Users className="w-5 h-5" />
            Employee Attendance
          </label>
          
          {/* Nobody Present Option */}
          <button
            onClick={handleNobodyPresentToggle}
            className={`w-full mb-4 h-14 rounded-lg border-2 font-bold flex items-center justify-center gap-3 ${
              nobodyPresent 
                ? 'border-red-500 bg-red-50 text-red-700' 
                : 'border-slate-300 bg-white text-slate-700 hover:border-red-300'
            }`}
          >
            <UserX className="w-6 h-6" />
            {nobodyPresent ? 'NOBODY PRESENT (SELECTED)' : 'NOBODY PRESENT'}
          </button>

          {!nobodyPresent && (
            <div className="bg-white rounded-lg border-2 border-slate-200 p-4 space-y-3">
              {manpowerList.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No employees assigned to this park</p>
              ) : (
                manpowerList.map((employee) => (
                  <label
                    key={employee.employee_id}
                    className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedEmployees.includes(employee.employee_id)
                        ? 'bg-green-100 border-2 border-green-500'
                        : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.employee_id)}
                      onChange={() => toggleEmployee(employee.employee_id)}
                      className="h-6 w-6 rounded border-2 border-slate-400 text-green-600"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{employee.employee_name}</div>
                      <div className="text-sm text-slate-500">{employee.employee_id}</div>
                    </div>
                    {selectedEmployees.includes(employee.employee_id) && (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    )}
                  </label>
                ))
              )}
              <p className="text-sm text-slate-600 mt-2">
                Selected: {selectedEmployees.length} / {manpowerList.length}
              </p>
            </div>
          )}

          {nobodyPresent && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
              <UserX className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <p className="text-red-700 font-medium">All workers will be marked as ABSENT</p>
              <p className="text-sm text-red-600 mt-1">Workers photo is not required</p>
            </div>
          )}
        </div>
        
        {/* Temporary Workers Section */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 block flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Temporary / Substitute Workers
          </label>
          
          {temporaryWorkers.length > 0 && (
            <div className="bg-orange-50 rounded-lg border-2 border-orange-200 p-4 mb-3 space-y-2">
              {temporaryWorkers.map((worker, index) => (
                <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
                  <div>
                    <div className="font-semibold text-slate-900">{worker.name}</div>
                    {worker.reason && (
                      <div className="text-sm text-slate-500">{worker.reason}</div>
                    )}
                  </div>
                  <button
                    onClick={() => removeTemporaryWorker(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
              {temporaryWorkers.length > 2 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-sm text-blue-700 font-medium">
                    More than 2 temporary workers added. Please capture additional photos below.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {showAddTempWorker ? (
            <div className="bg-white rounded-lg border-2 border-slate-200 p-4 space-y-3">
              <input
                type="text"
                value={newTempWorker.name}
                onChange={(e) => setNewTempWorker(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Worker Name"
                className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
              />
              <input
                type="text"
                value={newTempWorker.reason}
                onChange={(e) => setNewTempWorker(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason (e.g., Replacing Lakshmi - on leave)"
                className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
              />
              <div className="flex gap-3">
                <button
                  onClick={addTemporaryWorker}
                  className="flex-1 h-12 rounded-lg bg-green-600 text-white font-bold"
                >
                  ADD
                </button>
                <button
                  onClick={() => {
                    setShowAddTempWorker(false);
                    setNewTempWorker({ name: '', reason: '' });
                  }}
                  className="h-12 px-6 rounded-lg bg-slate-200 text-slate-700 font-bold"
                >
                  CANCEL
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTempWorker(true)}
              className="w-full h-14 rounded-lg border-2 border-dashed border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 font-bold flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              ADD TEMPORARY WORKER
            </button>
          )}
        </div>
        
        {/* Hidden file inputs */}
        <input type="file" accept="image/*" capture="environment" ref={workersPhotoRef} onChange={(e) => handlePhotoFile(e, 'workers')} className="hidden" />
        
        {/* Workers Photo - only required if not "Nobody Present" */}
        {!nobodyPresent && (
          <div>
            <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
              Workers Photo *
            </label>
            {workersPhoto ? (
              <div className="relative group">
                <img src={workersPhoto} alt="Workers" className="w-full h-48 object-cover rounded-lg" />
                {loading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <button
                  onClick={() => {
                    setWorkersPhoto(null);
                    setTimeout(() => triggerPhotoCapture('workers'), 100);
                  }}
                  disabled={loading}
                  className="absolute top-2 right-2 bg-white rounded-lg px-3 py-2 shadow-md text-sm font-bold disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Retake'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => triggerPhotoCapture('workers')}
                disabled={loading}
                className="h-48 w-full rounded-lg border-2 border-dashed border-slate-300 bg-white hover:bg-slate-50 flex flex-col items-center justify-center gap-2"
              >
                <Camera className="w-12 h-12 text-slate-400" />
                <span className="font-bold text-slate-600">{loading ? 'UPLOADING...' : 'TAP TO CAPTURE'}</span>
              </button>
            )}
          </div>
        )}

        {/* Additional Photos - For parks with >5 workers (OPTIONAL) */}
        {!nobodyPresent && needsAdditionalPhotos && (
          <div>
            <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Additional Photos (Optional - {additionalPhotos.length}/2)
            </label>
            <p className="text-xs text-slate-500 mb-3">
              This park has {parkData?.manpower_list?.length} workers. You can capture up to 2 additional photos.
            </p>
            
            {/* Show captured additional photos */}
            {additionalPhotos.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {additionalPhotos.map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img src={photo} alt={`Additional ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    <button
                      onClick={() => removeAdditionalPhoto(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add more button */}
            {additionalPhotos.length < 2 && (
              <>
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment" 
                  ref={additionalPhotoRef} 
                  onChange={handleAdditionalPhoto} 
                  className="hidden" 
                />
                <button
                  onClick={() => additionalPhotoRef.current?.click()}
                  disabled={loading}
                  className="h-24 w-full rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 flex flex-col items-center justify-center gap-1"
                >
                  <Plus className="w-8 h-8 text-blue-400" />
                  <span className="text-sm font-bold text-blue-600">ADD PHOTO</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* Tools Available */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 block flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Tools Available
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setToolsAvailable(true)}
              className={`h-14 rounded-lg border-2 font-bold flex items-center justify-center gap-2 ${
                toolsAvailable === true 
                  ? 'border-green-500 bg-green-100 text-green-700' 
                  : 'border-slate-300 bg-white'
              }`}
            >
              <CheckCircle2 className="w-5 h-5" />
              YES
            </button>
            <button
              onClick={() => setToolsAvailable(false)}
              className={`h-14 rounded-lg border-2 font-bold flex items-center justify-center gap-2 ${
                toolsAvailable === false 
                  ? 'border-red-500 bg-red-100 text-red-700' 
                  : 'border-slate-300 bg-white'
              }`}
            >
              <XCircle className="w-5 h-5" />
              NO
            </button>
          </div>
        </div>

        {/* Cleanliness */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 block flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Cleanliness Level
          </label>
          <div className="grid grid-cols-3 gap-3">
            {['Good', 'Average', 'Poor'].map((level) => (
              <button
                key={level}
                onClick={() => setCleanliness(level)}
                className={`h-14 rounded-lg border-2 font-bold ${
                  cleanliness === level
                    ? level === 'Good' ? 'border-green-500 bg-green-100 text-green-700'
                    : level === 'Average' ? 'border-yellow-500 bg-yellow-100 text-yellow-700'
                    : 'border-red-500 bg-red-100 text-red-700'
                    : 'border-slate-300 bg-white'
                }`}
              >
                {level.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Cleanliness Photos (up to 3) */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Cleanliness Photos * ({cleanlinessPhotos.length}/3)
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Capture up to 3 cleanliness photos. At least 1 is required.
          </p>
          
          {/* Show captured cleanliness photos */}
          {cleanlinessPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {cleanlinessPhotos.map((photo, idx) => (
                <div key={idx} className="relative">
                  <img src={photo} alt={`Cleanliness ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
                  <button
                    onClick={() => removeCleanlinessPhoto(idx)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add cleanliness photo button */}
          {cleanlinessPhotos.length < 3 && (
            <>
              <input 
                type="file" 
                accept="image/*" 
                capture="environment" 
                ref={cleanlinessPhotoRef} 
                onChange={handleCleanlinessPhoto} 
                className="hidden" 
              />
              <button
                onClick={() => cleanlinessPhotoRef.current?.click()}
                disabled={loading}
                className={`w-full rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 ${
                  cleanlinessPhotos.length === 0 
                    ? 'h-36 border-slate-300 bg-white hover:bg-slate-50' 
                    : 'h-20 border-green-300 bg-green-50 hover:bg-green-100'
                }`}
              >
                <Camera className={cleanlinessPhotos.length === 0 ? "w-10 h-10 text-slate-400" : "w-6 h-6 text-green-500"} />
                <span className={`font-bold ${cleanlinessPhotos.length === 0 ? 'text-slate-600' : 'text-green-600 text-sm'}`}>
                  {loading ? 'UPLOADING...' : cleanlinessPhotos.length === 0 ? 'TAP TO CAPTURE' : 'ADD ANOTHER PHOTO'}
                </span>
              </button>
            </>
          )}
        </div>

        {/* Remarks */}
        <div>
          <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Remarks
          </label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any additional notes..."
            rows={3}
            className="w-full p-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
          />
        </div>

        {/* Dynamic Questionnaires Section */}
        {activeQuestionnaires.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-purple-700 flex items-center gap-2 border-t pt-4">
              <ClipboardList className="w-5 h-5" />
              Additional Questionnaires
            </h2>
            
            {activeQuestionnaires.map((questionnaire) => (
              <div key={questionnaire.questionnaire_id} className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                <h3 className="font-bold text-purple-800 mb-1">{questionnaire.title}</h3>
                {questionnaire.description && (
                  <p className="text-sm text-purple-600 mb-3">{questionnaire.description}</p>
                )}
                
                <div className="space-y-4">
                  {questionnaire.questions.map((q, idx) => (
                    <div key={q.question_id} className="bg-white rounded-lg p-3">
                      <label className="font-medium text-slate-700 block mb-2">
                        Q{idx + 1}: {q.question_text}
                        {q.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      
                      {q.question_type === 'YES_NO' && (
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setQuestionnaireResponses(prev => ({
                              ...prev,
                              [questionnaire.questionnaire_id]: {
                                ...prev[questionnaire.questionnaire_id],
                                [q.question_id]: 'Yes'
                              }
                            }))}
                            className={`flex-1 py-2 rounded-lg font-bold transition-colors ${
                              questionnaireResponses[questionnaire.questionnaire_id]?.[q.question_id] === 'Yes'
                                ? 'bg-green-600 text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            YES
                          </button>
                          <button
                            type="button"
                            onClick={() => setQuestionnaireResponses(prev => ({
                              ...prev,
                              [questionnaire.questionnaire_id]: {
                                ...prev[questionnaire.questionnaire_id],
                                [q.question_id]: 'No'
                              }
                            }))}
                            className={`flex-1 py-2 rounded-lg font-bold transition-colors ${
                              questionnaireResponses[questionnaire.questionnaire_id]?.[q.question_id] === 'No'
                                ? 'bg-red-600 text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            NO
                          </button>
                        </div>
                      )}
                      
                      {q.question_type === 'DROPDOWN' && (
                        <select
                          value={questionnaireResponses[questionnaire.questionnaire_id]?.[q.question_id] || ''}
                          onChange={(e) => setQuestionnaireResponses(prev => ({
                            ...prev,
                            [questionnaire.questionnaire_id]: {
                              ...prev[questionnaire.questionnaire_id],
                              [q.question_id]: e.target.value
                            }
                          }))}
                          className="w-full p-2 border rounded-lg bg-white"
                        >
                          <option value="">-- Select --</option>
                          {q.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                      
                      {q.question_type === 'TEXT' && (
                        <textarea
                          value={questionnaireResponses[questionnaire.questionnaire_id]?.[q.question_id] || ''}
                          onChange={(e) => setQuestionnaireResponses(prev => ({
                            ...prev,
                            [questionnaire.questionnaire_id]: {
                              ...prev[questionnaire.questionnaire_id],
                              [q.question_id]: e.target.value
                            }
                          }))}
                          className="w-full p-2 border rounded-lg"
                          rows={2}
                          placeholder="Enter your answer..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
        <div className="max-w-md mx-auto">
          <button
            onClick={proceedToQuestionnairesOrSubmit}
            disabled={loading}
            className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg disabled:opacity-50"
          >
            {loading ? 'SUBMITTING...' : 'SUBMIT INSPECTION'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Inspection;
